import * as IF from "infrontjs";
import { trackPageView } from '../data/analytics.js';

const BACK_SVG = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6 L9 12 L15 18"/></svg>`;

const FLAG_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 21 V4"/><path d="M6 4 L18 4 L15 8 L18 12 L6 12"/></svg>`;
const MINE_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="13" r="6"/><path d="M12 4 V7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M5 7 L7 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M19 7 L17 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
const GOAL_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3 L14.6 9.1 L21.1 9.7 L16.2 14 L17.7 20.3 L12 16.9 L6.3 20.3 L7.8 14 L2.9 9.7 L9.4 9.1 Z"/></svg>`;
const START_SVG = `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="6"/></svg>`;


function cellHtml( kind, num )
{
    const cls = `pmh-cell pmh-cell--${ kind }`;
    if ( kind === 'safe' && num != null )
    {
        return `<span class="${ cls }" data-n="${ num }"><span class="pmh-cell__num">${ num }</span></span>`;
    }
    if ( kind === 'flag' )  return `<span class="${ cls }" aria-label="flag">${ FLAG_SVG }</span>`;
    if ( kind === 'mine' )  return `<span class="${ cls }" aria-label="mine">${ MINE_SVG }</span>`;
    if ( kind === 'goal' )  return `<span class="${ cls }" aria-label="goal">${ GOAL_SVG }</span>`;
    if ( kind === 'start' ) return `<span class="${ cls }" aria-label="start">${ START_SVG }</span>`;
    return `<span class="${ cls }"></span>`;
}

// Render an inline mini-board. `cells` is a sparse array of { r, c, kind, num? };
// missing cells are 'covered'. Optional `dashedPath` is an array of [r,c] points
// drawn as a dashed polyline overlay, used for the Path-mode illustration.
function miniBoardHtml( { rows, cols, cells, dashedPath, accent } )
{
    const map = {};
    cells.forEach( c => { map[ `${ c.r },${ c.c }` ] = c; });

    let cellsHtml = '';
    for ( let i = 0; i < rows * cols; i++ )
    {
        const r = Math.floor( i / cols );
        const c = i % cols;
        const cell = map[ `${ r },${ c }` ];
        cellsHtml += cell ? cellHtml( cell.kind, cell.num ) : cellHtml( 'covered' );
    }

    let pathHtml = '';
    if ( dashedPath && dashedPath.length > 1 )
    {
        const w = cols * 28 + ( cols - 1 ) * 3;
        const h = rows * 28 + ( rows - 1 ) * 3;
        const points = dashedPath.map( ( [ r, c ] ) => `${ c * 31 + 14 },${ r * 31 + 14 }` ).join( ' ' );
        pathHtml = `<svg class="pmh-pathline" width="${ w }" height="${ h }" aria-hidden="true">
            <polyline points="${ points }" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="4 4"/>
        </svg>`;
    }

    const style = `display: grid; grid-template-columns: repeat(${ cols }, 28px); grid-template-rows: repeat(${ rows }, 28px); gap: 3px;`;
    return `<div class="pmh-board pmh-board--${ accent || 'default' }" style="${ style }">${ cellsHtml }${ pathHtml }</div>`;
}

function ruleRow( n, title, body, extraHtml )
{
    return `<li class="pmh-rule">
        <span class="pmh-rule__num">${ n }</span>
        <div class="pmh-rule__body">
            <div class="pmh-rule__title">${ title }</div>
            ${ body ? `<p class="pmh-rule__text">${ body }</p>` : '' }
            ${ extraHtml || '' }
        </div>
    </li>`;
}

function legendHtml( items )
{
    const li = items.map( it => `
        <li class="pmh-legend__item">
            ${ cellHtml( it.kind, it.num ) }
            <span class="pmh-legend__label">${ it.label }</span>
        </li>` ).join( '' );
    return `<ul class="pmh-legend">${ li }</ul>`;
}

function generalSectionHtml()
{
    const tapHoldDemo = `<div class="pmh-inline">
        <span class="pmh-inline__cells">
            ${ cellHtml( 'covered' ) }
            ${ cellHtml( 'safe', 2 ) }
            ${ cellHtml( 'flag' ) }
        </span>
        <span class="pmh-inline__txt">Tap → reveal · Hold → flag</span>
    </div>`;

    const heartsDemo = `<div class="pmh-hearts-demo" aria-hidden="true">
        <span class="pmh-heart pmh-heart--full"></span>
        <span class="pmh-heart pmh-heart--full"></span>
        <span class="pmh-heart pmh-heart--empty"></span>
    </div>`;

    const rules = [
        ruleRow( 1, 'Tap to reveal a cell', 'If it&rsquo;s empty, the surrounding area opens up. If it&rsquo;s a number, that number tells you how many mines touch that cell &mdash; including diagonals.' ),
        ruleRow( 2, 'Long-press to plant a flag', 'Mark cells you&rsquo;re sure are mines. Flags are how you defuse &mdash; you can&rsquo;t lose on a flagged cell.', tapHoldDemo ),
        ruleRow( 3, 'Numbers count adjacent mines', 'A &ldquo;2&rdquo; means exactly two of the eight neighbors are mines. Combine numbers to deduce safe cells.' ),
        ruleRow( 4, 'Hearts are your lives', 'Stepping on a mine costs one heart. Lose them all and the level resets. Refill over time or earn them by clearing levels first try.', heartsDemo ),
        ruleRow( 5, 'Beat the timer', 'Each level has a soft countdown. The timer flashes red in the final 10 seconds &mdash; finish before zero or start over.' ),
    ].join( '' );

    const legend = legendHtml( [
        { kind: 'covered',           label: 'Hidden cell' },
        { kind: 'safe', num: 2,      label: 'Number &mdash; adjacent mines' },
        { kind: 'flag',              label: 'Flagged (defused)' },
        { kind: 'mine',              label: 'Mine' },
    ]);

    return `<section class="pmh-section pmh-section--general">
        <div class="pmh-section__head">
            <div class="pmh-section__eyebrow">The basics</div>
            <h3 class="pmh-section__title">General rules</h3>
            <p class="pmh-section__lede">Puzzlemines is a daily minesweeper. Hidden mines are scattered on a grid &mdash; your job is to read the numbers and finish each level without setting one off.</p>
        </div>
        <ol class="pmh-rules">${ rules }</ol>
        ${ legend }
    </section>`;
}

function modeClearSectionHtml()
{
    const board = miniBoardHtml({
        rows: 4, cols: 5, accent: 'clear',
        cells: [
            { r:0, c:0, kind:'safe', num:1 }, { r:0, c:1, kind:'safe', num:1 }, { r:0, c:2, kind:'flag' }, { r:0, c:3, kind:'safe', num:1 }, { r:0, c:4, kind:'covered' },
            { r:1, c:0, kind:'safe', num:1 }, { r:1, c:1, kind:'safe', num:2 }, { r:1, c:2, kind:'safe', num:2 }, { r:1, c:3, kind:'safe', num:1 }, { r:1, c:4, kind:'safe', num:1 },
            { r:2, c:0, kind:'flag' },        { r:2, c:1, kind:'safe', num:1 }, { r:2, c:2, kind:'safe' },     { r:2, c:3, kind:'safe' },        { r:2, c:4, kind:'flag' },
            { r:3, c:0, kind:'safe', num:1 }, { r:3, c:1, kind:'safe', num:1 }, { r:3, c:2, kind:'safe' },     { r:3, c:3, kind:'safe', num:1 }, { r:3, c:4, kind:'safe', num:1 },
        ],
    });

    return `<section class="pmh-section pmh-section--mode pmh-section--mode--clear">
        <div class="pmh-section__head">
            <div class="pmh-section__eyebrow">Mode 1 &middot; Clear All</div>
            <h3 class="pmh-section__title">Reveal every safe cell</h3>
            <p class="pmh-section__lede">The classic. Open every cell that isn&rsquo;t a mine. Flag the rest &mdash; no loose ends.</p>
        </div>
        <div class="pmh-mode__grid">
            <div class="pmh-mode__art pmh-mode__art--clear">${ board }</div>
            <ul class="pmh-mode__list">
                <li><strong>Goal:</strong> reveal every non-mine cell.</li>
                <li><strong>Win:</strong> the last safe cell auto-flags the remaining mines.</li>
                <li><strong>Tip:</strong> chase open areas &mdash; they cascade and give you free numbers.</li>
            </ul>
        </div>
    </section>`;
}

function modeFindSectionHtml()
{
    const board = miniBoardHtml({
        rows: 4, cols: 5, accent: 'find',
        cells: [
            { r:0, c:0, kind:'safe', num:1 }, { r:0, c:1, kind:'safe', num:1 }, { r:0, c:2, kind:'covered' }, { r:0, c:3, kind:'covered' }, { r:0, c:4, kind:'covered' },
            { r:1, c:0, kind:'covered' },     { r:1, c:1, kind:'safe', num:2 }, { r:1, c:2, kind:'covered' }, { r:1, c:3, kind:'covered' }, { r:1, c:4, kind:'covered' },
            { r:2, c:0, kind:'safe', num:1 }, { r:2, c:1, kind:'safe', num:1 }, { r:2, c:2, kind:'covered' }, { r:2, c:3, kind:'goal' },    { r:2, c:4, kind:'covered' },
            { r:3, c:0, kind:'safe' },        { r:3, c:1, kind:'safe' },        { r:3, c:2, kind:'covered' }, { r:3, c:3, kind:'covered' }, { r:3, c:4, kind:'covered' },
        ],
    });

    return `<section class="pmh-section pmh-section--mode pmh-section--mode--find">
        <div class="pmh-section__head">
            <div class="pmh-section__eyebrow">Mode 2 &middot; Find</div>
            <h3 class="pmh-section__title">Find the prize, leave the rest</h3>
            <p class="pmh-section__lede">A specific cell hides a star. Reveal it and you win &mdash; no need to clear the board.</p>
        </div>
        <div class="pmh-mode__grid">
            <div class="pmh-mode__art pmh-mode__art--find">${ board }</div>
            <ul class="pmh-mode__list">
                <li><strong>Goal:</strong> reveal the cell with the star.</li>
                <li><strong>Win:</strong> the moment you tap it &mdash; board state doesn&rsquo;t matter.</li>
                <li><strong>Tip:</strong> deduce its location from the numbers, don&rsquo;t brute-force the board.</li>
            </ul>
        </div>
    </section>`;
}

function modePathSectionHtml()
{
    const board = miniBoardHtml({
        rows: 4, cols: 5, accent: 'path',
        cells: [
            { r:0, c:0, kind:'start' },        { r:0, c:1, kind:'safe', num:1 }, { r:0, c:2, kind:'safe', num:1 }, { r:0, c:3, kind:'covered' }, { r:0, c:4, kind:'covered' },
            { r:1, c:0, kind:'safe', num:1 }, { r:1, c:1, kind:'safe', num:2 }, { r:1, c:2, kind:'safe', num:1 }, { r:1, c:3, kind:'covered' }, { r:1, c:4, kind:'covered' },
            { r:2, c:0, kind:'covered' },     { r:2, c:1, kind:'covered' },     { r:2, c:2, kind:'safe', num:1 }, { r:2, c:3, kind:'safe', num:2 }, { r:2, c:4, kind:'covered' },
            { r:3, c:0, kind:'covered' },     { r:3, c:1, kind:'covered' },     { r:3, c:2, kind:'covered' },     { r:3, c:3, kind:'safe', num:1 }, { r:3, c:4, kind:'goal' },
        ],
        dashedPath: [ [0,0],[0,1],[0,2],[1,2],[2,2],[2,3],[3,3],[3,4] ],
    });

    return `<section class="pmh-section pmh-section--mode pmh-section--mode--path">
        <div class="pmh-section__head">
            <div class="pmh-section__eyebrow">Mode 3 &middot; Path</div>
            <h3 class="pmh-section__title">Walk a safe route, start to goal</h3>
            <p class="pmh-section__lede">A start dot and a goal dot are marked. Reveal a connected chain of safe cells between them &mdash; orthogonal steps only.</p>
        </div>
        <div class="pmh-mode__grid">
            <div class="pmh-mode__art pmh-mode__art--path">${ board }</div>
            <ul class="pmh-mode__list">
                <li><strong>Goal:</strong> uncover a connected path from ${ cellHtml( 'start' ) } to ${ cellHtml( 'goal' ) }.</li>
                <li><strong>Win:</strong> moment the chain connects &mdash; the rest can stay covered.</li>
                <li><strong>Tip:</strong> you don&rsquo;t need the shortest route, just a safe one.</li>
            </ul>
        </div>
    </section>`;
}

export class HelpState extends IF.State
{
    static ID = 'help';
    static ROUTE = '/help';

    async enter()
    {
        trackPageView( 'HelpState', '/help' );

        const appEl = document.getElementById( 'app' );
        const gameEl = document.getElementById( 'game-container' );
        appEl.classList.add( 'active' );
        gameEl.classList.remove( 'active' );

        this.app.view.renderHtml( this.app.container, `
            <div class="pmh">
                <header class="pmh__top">
                    <button class="pmh__back" id="pmh-back" type="button" aria-label="Back">${ BACK_SVG }</button>
                    <div class="pmh__title">How to Play</div>
                    <div class="pmh__topSpacer" aria-hidden="true"></div>
                </header>

                <div class="pmh__scroll">
                    ${ generalSectionHtml() }

                    <div class="pmh__modesIntro">
                        <div class="pmh__modesEyebrow">Three game modes</div>
                        <h3 class="pmh__modesTitle">Every level is one of three challenges</h3>
                        <p class="pmh__modesLede">The HUD shows the active mode at the top of every level. The objective changes &mdash; but the rules above always apply.</p>
                    </div>

                    ${ modeClearSectionHtml() }
                    ${ modeFindSectionHtml() }
                    ${ modePathSectionHtml() }

                    <footer class="pmh__footer">
                        <p>That&rsquo;s it. <strong>999 levels</strong> across the three modes. Good luck out there.</p>
                    </footer>
                </div>
            </div>
        `);

        document.getElementById( 'pmh-back' ).addEventListener( 'click', () =>
        {
            this.app.router.redirect( '/' );
        });
    }

    async exit()
    {
        this.app.container.innerHTML = '';
        document.getElementById( 'app' ).classList.remove( 'active' );
    }
}
